import React, { createContext, useCallback, useEffect, useState } from "react";
import AnswerNode from "../components/nodes/AnswerNode";
import DtmfNode from "../components/nodes/DtmfNode";
import HangupNode from "../components/nodes/HangupNode";
import PlayNode from "../components/nodes/PlayNode";
import TransferNode from "../components/nodes/TransferNode";
import CustomEdge from "../components/edges/ButtonEdge";
import ReactFlow, {
  addEdge,
  MarkerType,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from "react-flow-renderer";
import { fetchAndSetFlashMessage, Fetcher } from "../../../Helpers/fetcher";
import { Notify } from "../../../Helpers/notification";
import { isEmpty } from "lodash";
import { ACTION_COLORS, DTMF_KEYS, IVR_SETTING_ORDER } from "../../../Helpers/constant";
import { NodesContext } from "../provider/IvrContext";
import { transformNodes, validateIVR } from "../../../Helpers/utils";
import RecordNode from "../components/nodes/RecordNode";
import Marquee from "react-fast-marquee";

const nodeTypes = {
  answerNode: AnswerNode,
  playNode: PlayNode,
  gatherNode: DtmfNode,
  transferNode: TransferNode,
  hangupNode: HangupNode,
  recordNode: RecordNode
};
const edgeTypes = { custom: CustomEdge };

const AddIVR = ({ app }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [nodeConnections, setNodeConnections] = useState({});
  const [flowName, setFlowName] = useState("");
  const [actions, setActions] = useState({});
  const [error, setError] = useState("");
  const [isGatherNode, setIsGatherNode] = useState(0);
  const [alertMessage, setAlertMessage] = useState("");

  const [smsWebhookFlag, setSmsWebhookFlag] = useState(false);
  const [smsWebhookList, setSmsWebhookList] = useState([]);

  const [audioList, setAudioList] = useState([]);

  const [timeScheduleModel, setTimeScheduleModel] = useState(false);
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("00:00");
  const [weekDays, setWeekDays] = useState([]);
  const [isWholeDay, setIsWholeDay] = useState(false);
  const [isWholeWeek, setIsWholeWeek] = useState(false);
  const [scheduleData, setScheduleData] = useState({});

  useEffect(() => {
    fetchAndSetFlashMessage(
      app.apiPath,
      msg => msg.moduleName === "custom_ivr" && msg.action === "add",
      msg => setAlertMessage(msg),
      null,
      null,
      app.userId
    );
    getIvrSettings();
    addNewNode("answer");
    getAudioPrompt()
    getModuleDetails();
  }, []);

  const getAudioPrompt = async () => {
    try {
      const resp = await Fetcher.get(`${app.apiPath}/api/obd/prompts/${app.userId}`);

      const welcomeFiles = resp.filter(
        (item) =>
          item.promptCategory === "welcome" &&
          item.promptStatus === 1
      );

      setAudioList(welcomeFiles);
    } catch (error) {
      console.error("Error fetching audio prompts:", error);
    }
  };

  const getIvrSettings = async () => {
    try {
      const resp = await Fetcher.get(`${app.apiPath}/api/obd/ivr/settings`);
      const order = IVR_SETTING_ORDER;
      const map = {};
      resp.forEach((i) => (map[i.action] = i.name));

      const ordered = {};
      order.forEach((k) => map[k] && (ordered[k] = map[k]));
      setActions(ordered);
    } catch (e) {
      console.error(e);
    }
  };

  const getModuleDetails = async () => {
    try {
      const res = await Fetcher.get(
        `${app.apiPath}/api/obd/module/list/${app.userId}`
      );
      if (!isEmpty(res)) {
        const hasSms = res.some((m) => m.moduleName === "SMS Webhooks");
        if (hasSms) {
          setSmsWebhookFlag(true);
          getSmsWebhookList();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getSmsWebhookList = async () => {
    const res = await Fetcher.get(
      `${app.apiPath}/api/obd/sms/webhooks/${app.userId}`
    );
    setSmsWebhookList(res);
  };


  useEffect(() => { updatedConnections() }, [edges])

  const updatedConnections = () => {
    const result = edges.reduce((acc, { source, target, sourceHandle }) => {
      const key = sourceHandle ?? source;
      if (!acc[key]) acc[key] = [];
      acc[key].push(target);
      return acc;
    }, {});
    setNodeConnections(result);
  }

  const generateTimeId = () => {
    return Date.now().toString(36);
  };
  const addNewNode = (type) => {
    setNodes((prev) => {
      const isFirstNode = prev.length === 0;
      const nodeOffset = 50;

      const offsetX = (prev.length % 5) * nodeOffset;
      const offsetY = (prev.length % 3) * nodeOffset;

      const finalPosition = isFirstNode
        ? { x: 500, y: 10 }
        : { x: 400 + offsetX, y: 10 + offsetY };

      if (type === "gather") {
        setIsGatherNode((c) => c + 1);
      }

      return [
        ...prev,
        {
          id: generateTimeId(),
          type: `${type}Node`,
          position: finalPosition,
          data: {
            label: `${type} Node`,
            verb: type === "gather" ? "dtmf" : type,
            userId: app.userId,
            apiPath: app.apiPath,
            onRemove,
          },
        },
      ];
    });
  };

  const updateNodeData = useCallback((nodeId, updatedData) => {
    setNodes((nds) => {
      const prevNode = nds.find(n => n.id === nodeId);
      const prevDigits = Number(prevNode?.data?.numDigits ?? 1);
      const nextDigits =
        updatedData.numDigits !== undefined
          ? Number(updatedData.numDigits)
          : prevDigits;

      if (prevDigits !== nextDigits) {
        setEdges((eds) =>
          eds.filter((edge) => {
            if (edge.source !== nodeId) return true;

            // multi → single
            if (nextDigits === 1) {
              return edge.sourceHandle !== `${nodeId}_single_source`;
            }

            // single → multi
            return !edge.sourceHandle?.startsWith(`${nodeId}_handle-`);
          })
        );
      }

      const removedHandles = [];
      if (updatedData.selectedHandles && prevNode?.data?.selectedHandles) {
        Object.keys(prevNode.data.selectedHandles).forEach((hid) => {
          if (!updatedData.selectedHandles.hasOwnProperty(hid)) {
            removedHandles.push(hid);
          }
        });
      }

      if (removedHandles.length > 0) {
        setEdges((eds) =>
          eds.filter(
            (edge) =>
              !(edge.source === nodeId && removedHandles.includes(edge.sourceHandle))
          )
        );
      }

      return nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, ...updatedData } }
          : node
      );
    });
  }, [setNodes, setEdges]);

  const onRemove = (id) => {
    setNodes((n) => n.filter((x) => x.id !== id));
    setEdges((e) => e.filter((x) => x.source !== id && x.target !== id));
  };

  const setParentForTarget = useCallback((targetId, parentLabel, idToRemove) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === targetId
          ? { ...n, data: { ...n.data, parent: parentLabel, idToRemove } }
          : n
      )
    );
  }, []);

  const addFlowEdge = useCallback((edgeConfig) => {
    setEdges((eds) => addEdge(edgeConfig, eds));
  }, []);

  const onConnect = useCallback((connection) => {
    const { source, target, sourceHandle } = connection;
    const sourceNode = nodes.find((n) => n.id === source);
    if (!sourceNode) return;
    const sourceVerb = sourceNode.data?.verb;

    const baseEdge = {
      ...connection,
      type: "custom",
      markerEnd: { type: MarkerType.Arrow },
    };

    if (sourceVerb === "dtmf") {
      const { numDigits = 1 } = sourceNode.data;
      if (Number(numDigits) === 1) {
        if (nodeConnections[sourceHandle]?.length > 0) {
          Notify.notify("Each DTMF option can connect only once");
          return {};
        }
        const handleIndex = Number(sourceHandle?.split("-")[1]);
        const label = DTMF_KEYS[handleIndex]
        addFlowEdge({
          ...baseEdge,
          label: `dtmf: ${label}`,
          labelStyle: { fill: "blue", fontWeight: "bold" },
          data: {
            label: `dtmf: ${label}`
          }
        });

        const idToRemove = `${sourceNode.id}_handle-${label}`
        setParentForTarget(target, sourceNode.data.label, idToRemove);
        return;
      } else {
        if (nodeConnections[source]?.length >= 1) {
          Notify.notify("This node can only have one next step");
          return;
        }

        addFlowEdge(baseEdge);
        setParentForTarget(target, sourceNode.data.label);
        return;
      }
    }

    if (sourceVerb !== "gather" && (nodeConnections[source]?.length || 0) >= 1) {
      Notify.notify("A node can only have one child!");
      return {};
    }
    addFlowEdge(baseEdge);
    setParentForTarget(target, sourceNode.data.label);
  }, [nodes, nodeConnections, addFlowEdge, setParentForTarget]);

  const saveData = async () => {
    if (!validateIVR({ edges, flowName, nodes })) return;
    const data = transformNodes({ edges, flowName, userId: app.userId, nodesArray: nodes, isGatherNode, scheduleData, smsWebhookFlag });
    const payload = {
      nodeData: JSON.stringify(data),
      edgesData: JSON.stringify(edges),
      flowName,
      userId: app.userId,
      is_gather_node: isGatherNode,
      promptDuration: data.promptDuration
    };

    await Fetcher.post(`${app.apiPath}/api/obd/add/custom/ivr`, {
      headers: { "Content-type": "application/json" },
      method: 'POST',
      body: JSON.stringify(payload),
    });

    Notify.notify("IVR Created Successfully");
    window.location.href = "/ivr-list";
  };

  const handleReset = () => {
    setNodes([]);
    setEdges([]);
    setNodeConnections({})
    setFlowName("");
    setError("");
    setIsGatherNode(0);

    addNewNode("answer");
  };

  return (
    <NodesContext.Provider
      value={{ nodes, setNodes, smsWebhookFlag, smsWebhookList, updateNodeData, onRemove, audioList, apiPath: app.apiPath, nodeConnections }}
    >
      <div
        style={{
          height: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: 0, paddingRight: 80 }}>
          {alertMessage && <Marquee className="fade alert alert-success show">{alertMessage}</Marquee>}
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff",
              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                value={flowName}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^a-zA-Z0-9_]/g, "");
                  setError(v !== e.target.value ? "Invalid characters" : "");
                  setFlowName(v);
                }}
                placeholder="IVR Name"
                style={{
                  flex: 1,
                  padding: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  width: "250px",
                }}
              />
              <button onClick={saveData} className="btn btn-primary">
                Save IVR
              </button>
            </div>
            {error && <small style={{ color: "red" }}>{error}</small>}
            <div
              style={{
                position: "absolute",
                top: "67px",
                right: "20px",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: "100px",
                  backgroundColor: "#fff",
                  boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  padding: "5px 0",
                }}
              >
                {Object.keys(actions).map((type) => (
                  <div
                    key={type}
                    style={{
                      backgroundColor: ACTION_COLORS[type] || "#007bff",
                      color: "white",
                      textAlign: "center",
                      cursor:
                        actions[type] === "Answer" ? "not-allowed" : "pointer",
                      padding: "10px",
                      margin: "5px 10px",
                      borderRadius: "4px",
                      opacity: actions[type] === "Answer" ? 0.5 : 1,
                      transition: "background-color 0.3s",
                    }}
                    onClick={() => {
                      if (actions[type] !== "Answer") {
                        addNewNode(type);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (actions[type] !== "Answer") {
                        e.target.style.opacity = 0.8;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (actions[type] !== "Answer") {
                        e.target.style.opacity = 1;
                      }
                    }}
                  >
                    {actions[type] === "Input" ? "DTMF" : actions[type]}
                  </div>
                ))}
              </div>
              <div
                style={{
                  width: "100px",
                  marginTop: "10px",
                  backgroundColor: "#f8d7da",
                  color: "#dc3545",
                  textAlign: "center",
                  cursor: "pointer",
                  padding: "10px",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  boxShadow: "0 0 10px rgba(0,0,0,0.2)",
                  transition: "background-color 0.3s",
                }}
                onClick={handleReset}
                onMouseEnter={(e) => {
                  e.target.style.opacity = 0.8;
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = 1;
                }}
              >
                Reset All
              </div>
            </div>
          </div>
        </div>

        <div style={{ flexGrow: 1 }}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onConnect={onConnect}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
            />
          </ReactFlowProvider>
        </div>
      </div>
    </NodesContext.Provider>
  );
};
export default AddIVR;
